import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import root from 'react-shadow';
import { useCss } from 'entities/style';
import { useSelectedTemplates } from 'entities/template';
import { useGetDocument, useGetDocumentTableOfContent } from 'entities/document';
import { useUser } from 'app/state/contexts';
import { HoveredTarget, buildHoverModifier } from '../utils/builtin-selectors';
import { AreaConfig, AreaContent, renderArea } from './preview-markup';

const HEADER_FOOTER_HIGHLIGHT_CSS = `
  .dc-hover-subarea-headers .subarea-headers,
  .dc-hover-subarea-pagenumber .subarea-pagenumber,
  .dc-hover-subarea-texts .subarea-texts,
  .dc-hover-tag-ol ol,
  .dc-hover-tag-li li,
  .dc-hover-tag-a a {
    outline: 2px solid #547ba2;
    outline-offset: 1px;
  }
`;

interface PreviewDocContent {
  header?: AreaConfig;
  footer?: AreaConfig;
  metadata?: { texts?: Record<string, Record<string, string>> };
}

interface PreviewDoc {
  revisionId?: string;
  content?: PreviewDocContent;
}

interface HeaderFooterPreviewProps {
  hoveredInner?: HoveredTarget | null;
}

export function HeaderFooterPreview({ hoveredInner }: HeaderFooterPreviewProps = {}) {
  const { t } = useTranslation(['common', 'dc']);

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const { data: selectedTemplates } = useSelectedTemplates(documentId);
  const css = useCss({ documentSuperId: documentId, documentRevisionId: undefined, useDraft: true, withStyle: undefined, embedd: undefined });

  const pData = pDocument.data as PreviewDoc | undefined;
  const docContent: PreviewDocContent = pData?.content ?? {};
  const headerConfig: AreaConfig | undefined = docContent.header;
  const footerConfig: AreaConfig | undefined = docContent.footer;
  const metadataTexts: Record<string, Record<string, string>> = docContent.metadata?.texts ?? {};
  const localeKeys = Object.keys(metadataTexts);
  const previewLocale = localeKeys[0];
  const showsHeadings = (config?: AreaConfig) => config?.type === 'headings' || config?.type === 'combined';
  const needsHeadings = showsHeadings(headerConfig) || showsHeadings(footerConfig);
  const tocQuery = useGetDocumentTableOfContent(needsHeadings ? pData?.revisionId ?? null : null, { locale: previewLocale });

  if (css.isLoading || !css.data) {
    return (
      <div
        className="header-footer-preview text-muted fs-7 d-flex align-items-center justify-content-center"
        style={{ height: 140, border: '1px dashed #dee2e6', borderRadius: 4 }}
      >
        {t('common:loading')}
      </div>
    );
  }

  const templateSuperId = selectedTemplates?.[0] ?? '';
  const headings = (tocQuery.data?.content as Array<{ uuid: string; name: string }> | undefined) ?? undefined;
  const previewContent: AreaContent = {
    texts: previewLocale ? metadataTexts[previewLocale] : undefined,
    headings,
  };

  const headerHoverModifier = buildHoverModifier('area-header', hoveredInner);
  const footerHoverModifier = buildHoverModifier('area-footer', hoveredInner);

  return (
    <div className="header-footer-preview mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
      <root.div>
        <style>{css.data.shadowedCss}</style>
        <style>{HEADER_FOOTER_HIGHLIGHT_CSS}</style>
        <style>{`
          .preview-stub {
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            text-align: center;
            color: #adb5bd;
            font-size: 11px;
            font-style: italic;
            letter-spacing: 0;
            text-transform: none;
            pointer-events: none;
          }
        `}</style>
        <div
          className={`page-content xrp-template-${templateSuperId}`}
          style={{ position: 'relative', width: '100%', height: 160, overflow: 'hidden', boxSizing: 'border-box' }}
        >
          {renderArea('header', headerConfig, previewContent, headerHoverModifier)}
          <div className="preview-stub">{t('dc:preview.page-content-stub')}</div>
          {renderArea('footer', footerConfig, previewContent, footerHoverModifier)}
        </div>
      </root.div>
    </div>
  );
}
